import { useEffect, useState } from 'react';
import { api } from './api';
import type { Ability } from './permissions';
import { NAV_GROUPS, NAV_STANDALONE, isNavItemOffered, type NavItem } from './nav';
import type { PreferencesDocument } from './types';

/**
 * WHERE SOMEBODY LANDS AFTER SIGNING IN.
 *
 * Two things live here and they are deliberately separate: the pure decision
 * (`resolveLandingPath`) and the transport that feeds it (`useStoredLandingPath`).
 * The decision is the part with rules in it, and it is the part that has to be
 * readable without knowing anything about `/preferences`.
 *
 * ---------------------------------------------------------------------------
 * THERE ARE NOW TWO SOURCES, AND THE PER-USER ONE WINS
 * ---------------------------------------------------------------------------
 *
 * ⚠️ *SUPERSEDED, the heading and its conclusion: "WHY IT IS A PER-USER
 * PREFERENCE AND NOT A PERMISSION SET — Owner's decision: the landing page
 * belongs to the PERSON. Ranking permission sets was considered and rejected."*
 * **A permission set carries a landing page as well, since 2026-09-04**, and a
 * super admin sets it on `/permission-sets/:id`.
 *
 * **The ARGUMENT that killed the first attempt is untouched, and it is why the
 * new one is shaped the way it is.** This app still cannot rank sets:
 * **`/permissions/me` returns a flat permission list with the sets stripped
 * out**, so it cannot learn which sets a user holds, let alone their order — and
 * the migration that created permission sets says their order "is not meaningful
 * and never should be". So the tie-break was not moved to the client; **it was
 * moved to the SERVER**, which publishes one already-resolved `landingPath` on
 * `/permissions/me`. Nothing here ranks anything, which is the half of the old
 * ruling that mattered.
 *
 * ⚠️ **The two sources are NOT merged and must never be.** They answer different
 * questions — *where does this administrator want holders of this set to start*
 * versus *where does this person want to start* — and `resolveLandingPathWithSet`
 * below is the single place that says which wins. See its own comment for the
 * precedence and the reason.
 *
 * `/preferences` remains the home of the PER-USER half, for the reason it always
 * was: it is per-user, server-side, and — unlike `/permissions/me` —
 * **deliberately not cached for the session**. A landing page that only took
 * effect at the next sign-in would be a setting you change and cannot observe.
 * ⚠️ The set-derived half **is** cached for the session, because it rides on
 * `/permissions/me`, so an administrator's change to a set lands at the holder's
 * next sign-in — the same lag every other permission-set edit has, and the
 * permission-set screens already say so.
 *
 * ---------------------------------------------------------------------------
 * ⚠️ THE DOCUMENT IS UNVALIDATED, SO THE VALIDATION HAS TO HAPPEN HERE
 * ---------------------------------------------------------------------------
 *
 * `/preferences` accepts any key at any type and stores it. **A misspelled key
 * or a misspelled value is a `200` that does nothing, and nothing on either side
 * will tell you.** So a stored landing page is an untrusted string, and this
 * module treats it as one:
 *
 *   - a non-string, or a blank one, is read as "nothing stored";
 *   - a path this app does not route — `/dashbord`, or `/dashboard` now that
 *     the Overview page is gone — falls back **silently** to the default,
 *     because the alternative is a 404 on every single sign-in;
 *   - a path the reader cannot SEE falls back too. See below; that one is the
 *     rule most likely to be argued away.
 *
 * ⚠️ **An unreachable landing page is a reachable state, and it is a trap the
 * person cannot get out of.** A stored `/audit-log` on an account below the
 * admin tier lands them on a permissions refusal every time they sign in — and
 * the screen where they would change the setting is one they can no longer
 * reach past. So an unreachable stored value resolves to the first page they
 * CAN see, per the owner's earlier ruling on the same shape.
 *
 * ⚠️ **Silently, and that is on purpose.** A banner saying "your landing page no
 * longer exists" on every sign-in is a permanent apology for a setting nobody
 * remembers making, and it would be the first thing the app said to somebody
 * every morning. The recovery is the same either way: open the page you want.
 */

/**
 * Where a sign-in goes when nothing else decides.
 *
 * ⚠️ **This page is SCOPED TO THE VIEWER AS ACCOUNT LEAD** — every table on it
 * filters by `accountLeadId`. Somebody who is not one of the four account leads
 * lands on five empty tables and a funnel of zeros. That is a real cost and the
 * owner accepted it knowingly; **do not "improve" it with a fallback that sends
 * a non-lead somewhere else.** A landing page that silently sends two people to
 * two different screens is worse than one that is honestly empty for some of
 * them, and an empty table is a true statement about that person's accounts.
 */
export const DEFAULT_LANDING_PATH = '/university-partnerships';

/**
 * The top-level key inside the preferences document.
 *
 * A sibling of `tables` (see `preferences-store.ts`), never nested inside it —
 * that branch is the saved-filter scopes, keyed by page, and its whole-set
 * replace logic would treat a landing page as one more filter key to delete.
 */
export const LANDING_PREFERENCE_KEY = 'landingPage';

/**
 * Read the stored value out of a preferences document.
 *
 * Exported so the setting can be READ by whatever screen eventually offers to
 * change it, without that screen re-deriving what a valid value looks like.
 */
export function storedLandingPath(document: PreferencesDocument): string | null {
  const raw = document[LANDING_PREFERENCE_KEY];
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * Every in-app destination this reader can actually open, in sidebar order.
 *
 * ⚠️ **Derived from the nav, never from a list written down here.** The nav is
 * already the one place that knows which top-level pages exist and what gates
 * each one, so a second list would be a second answer to "where can this person
 * go" — and it would go stale in the silent direction, offering a landing page
 * to a route that had been deleted. Deleting a row here removes it as a landing
 * page in the same edit, which is exactly what happened to `/dashboard`.
 *
 * The three gates are the same three `Sidebar.tsx` applies, in the same order,
 * for the same reasons. ⚠️ **The `module` test DEGRADES OPEN**: a module absent
 * from the reader's permission list is allowed, and only an explicit `none`
 * scope denies. So a failed `/permissions/me` leaves every page landable, which
 * is the direction that cannot strand anybody.
 *
 * A routeless row ("Soon") and an EXTERNAL row are both excluded by the `to`
 * test, structurally rather than by a check somebody has to remember. Neither is
 * somewhere a sign-in may land: one is not built, and the other is a document in
 * somebody else's system that this app cannot promise resolves at all.
 */
export function landingCandidates(ability: Ability): readonly string[] {
  return landingPageOptions(ability).map((option) => option.path);
}

/** One landable destination: the path to store, and the page's real name. */
export interface LandingPageOption {
  readonly path: string;
  readonly label: string;
}

/**
 * `landingCandidates`, but carrying each page's NAME as well as its path.
 *
 * The paths alone are enough to VALIDATE a stored value, which is all
 * `resolveLandingPath` ever needed. A picker has to be able to say
 * `University Partnerships Dashboard` rather than `/university-partnerships`,
 * and the nav already holds that word — so this is the same one filter, and the
 * path-only list above is derived from it rather than written twice. Two walks
 * of the nav would agree the day they were written and diverge the first time
 * somebody tightened a gate in one of them, and the divergence is silent: a
 * picker would simply offer a page the resolver then refuses, which reads as the
 * setting not saving.
 *
 * ⚠️ **`fullLabel ?? label`, exactly as `Sidebar.tsx` and `GlobalSearch.tsx` do
 * it.** `label` is allowed to be an abbreviation; the full product name is what
 * assistive tech announces, and a picker naming a page is the same job.
 */
export function landingPageOptions(ability: Ability): readonly LandingPageOption[] {
  const rows: NavItem[] = [...NAV_GROUPS.flatMap((group) => [...group.items]), ...NAV_STANDALONE];

  /*
   * ⚠️ **`isNavItemOffered`, not a fourth hand-written copy of its three tests
   * — corrected 2026-09-04.** The three were inlined here, byte-identical to the
   * helper, which is precisely the arrangement the paragraph above warns about
   * and which nothing would have failed on: they agreed on the day they were
   * written and would have diverged the first time somebody tightened a gate in
   * one of them. That tightening then happened — `module` spread from the eight
   * Dashboards and Trackers rows onto the Modules rows and Integrations — and a
   * divergence here is silent in the worst direction: the picker offers a page
   * the resolver refuses, which reads as the setting not saving.
   *
   * The `to` test stays local. `isNavItemOffered` answers "may this reader be
   * offered this row", which a routeless "Soon" row can pass; a landing page has
   * to be somewhere you can actually go.
   */
  return rows
    .filter((item) => item.to !== undefined && isNavItemOffered(item, ability))
    .map((item) => ({ path: item.to as string, label: item.fullLabel ?? item.label }));
}

/**
 * The destination, given what is stored and who is asking.
 *
 * Pure, and total: it always returns a path. There is no "we could not decide"
 * branch, because the caller's only alternative would be a blank screen or a
 * bounce back to `/login`.
 *
 * The order is stored → default → first reachable, and each step is a fallback
 * for a different failure:
 *
 *   1. the stored value, when it names a page this reader can open;
 *   2. `DEFAULT_LANDING_PATH`, for the ordinary case where nothing is stored
 *      and for a stored value that has been deleted, misspelled or locked away;
 *   3. the first page in sidebar order they can open, for the reader whose
 *      permission set explicitly denies the default itself.
 *
 * ⚠️ Step 3 falls back to the default anyway when NOTHING is reachable. That
 * cannot happen against today's nav — Projects, Products, Companies, Deals,
 * Quotes, Invoices and the catalog carry no gate at all — and if it ever does,
 * sending somebody to a page that says why is better than sending them nowhere.
 *
 * ⚠️ **The PERMISSION SET's landing page is not consulted here**, and the
 * signature is why: this function knows only what the person stored. It is kept
 * as-is because two live callers pass exactly that (`App.tsx`, `LoginPage.tsx`),
 * and it now DELEGATES to `resolveLandingPathWithSet` rather than holding a
 * second copy of the ladder — one rung longer, one rung the same. **Widening a
 * caller to the set-aware answer means calling that function, not changing
 * this one**: a silently widened `resolveLandingPath` would move where two
 * screens send people with nothing in either file to say so.
 */
export function resolveLandingPath(stored: string | null, ability: Ability): string {
  return resolveLandingPathWithSet(stored, null, ability);
}

/**
 * The destination when BOTH sources may have an opinion.
 *
 * `stored` is the person's own `/preferences` value; `fromSets` is the
 * already-resolved `landingPath` off `/permissions/me`, which is what their
 * permission sets say. **Pass `undefined` or `null` for `fromSets` against a
 * server that does not serve the key yet** — the two are treated identically
 * here, because "no set names one" and "this API has no such field" both mean
 * *the sets have nothing to say*, and the caller has nothing to act on either
 * way. (The distinction is real and load-bearing on the EDITING screen, where an
 * absent field means a control must not be offered at all; it buys nothing at
 * this end of the wire.)
 *
 * ---------------------------------------------------------------------------
 * ⚠️ THE PRECEDENCE: THE PERSON'S OWN PREFERENCE WINS OVER THE SET'S
 * ---------------------------------------------------------------------------
 *
 * **A preference somebody set for themselves must not be overridden by an
 * administrator's default.** The set-level value is a DEFAULT — a sensible
 * starting page for whoever holds the set, chosen by somebody who has never met
 * most of them — where the per-user value is a decision by the one person it
 * affects, taken on their own screen, about their own morning.
 *
 * The failure the other order produces is the reason, and it is silent: an
 * administrator retunes a system set, and every holder who had deliberately
 * chosen a different landing page is moved off it with no notification, no
 * error, and nothing on any screen to connect the change to. They would report
 * "my setting stopped working", and the setting would still be stored, still be
 * valid, and still be losing. Under this order the administrator's value simply
 * does not reach anybody who has said otherwise, which is a thing they can see
 * on their own account and nobody has to diagnose.
 *
 * The default is unaffected either way: somebody who has expressed no preference
 * gets the set's page, which is exactly what the set exists to provide.
 *
 * ---------------------------------------------------------------------------
 *
 * The full ladder is stored → set → default → first reachable, and **every rung
 * goes through the same reachability test**. That matters most on the set rung:
 * a set may name a page some of its holders cannot open — role gates and
 * permission gates are separate mechanisms, and nothing stops an administrator
 * choosing `/audit-log` — and an unreachable landing page is a trap somebody
 * cannot get out of, because the screen where they would change it is behind
 * the refusal they land on. See the module header.
 */
export function resolveLandingPathWithSet(
  stored: string | null,
  fromSets: string | null | undefined,
  ability: Ability,
): string {
  const reachable = landingCandidates(ability);
  if (stored !== null && reachable.includes(stored)) return stored;
  if (fromSets != null && reachable.includes(fromSets)) return fromSets;
  if (reachable.includes(DEFAULT_LANDING_PATH)) return DEFAULT_LANDING_PATH;
  return reachable[0] ?? DEFAULT_LANDING_PATH;
}

/**
 * How long to wait for `/preferences` before going to the default.
 *
 * ⚠️ **A pending fetch and a failed one must end the same way.** Without a cap,
 * a request that never settles holds somebody on a "taking you in" screen for
 * ever — and the failure would look like a broken sign-in rather than a slow
 * one. Four seconds is long enough that the setting works on a bad connection
 * and short enough that nobody reads the wait as a hang.
 */
const PREFERENCES_WAIT_MS = 4_000;

/** `pending` while the answer may still change; `settled` once it cannot. */
export type LandingPathState =
  | { readonly status: 'pending' }
  | { readonly status: 'settled'; readonly stored: string | null };

/**
 * Read the caller's stored landing page, once.
 *
 * ⚠️ **`enabled` is a real gate, not an optimisation.** The hook must not fire
 * before there is a token to fire with, and it must not fire at all when the
 * caller already knows where it is going — a deep link captured by
 * `RequireAuth` wins outright, so asking for a preference nobody will read is a
 * request spent for nothing on the one screen where the person is waiting.
 *
 * ⚠️ **Every failure settles to `stored: null`, which is "use the default".**
 * This mirrors `loadPermissions`, which never throws and downgrades to role-only
 * gating: a preference is a nicety, and nothing about it may stand between
 * somebody and the app they just signed in to.
 */
export function useStoredLandingPath(enabled: boolean): LandingPathState {
  const [state, setState] = useState<LandingPathState>({ status: 'pending' });

  useEffect(() => {
    if (!enabled) return;

    const controller = new AbortController();
    /*
     * Settles the state at most once, so the timeout below and the response
     * cannot both write — whichever arrives first is the answer, and a late
     * response must not move somebody who has already been sent on their way.
     */
    let done = false;
    const settle = (stored: string | null) => {
      if (done || controller.signal.aborted) return;
      done = true;
      setState({ status: 'settled', stored });
    };

    const timer = window.setTimeout(() => settle(null), PREFERENCES_WAIT_MS);

    api
      .getPreferences(controller.signal)
      .then((response) => settle(storedLandingPath(response.data.preferences)))
      .catch(() => settle(null));

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [enabled]);

  return state;
}
